import signUpTypes from './types'
import ecbrApi from '../../../services/ecbrApi'
import {
  generateErrorMessage,
  generateSignupPurchaseNewBrandPayload,
  generateSignUpPurchasePayload
} from './helpers'
import { PaymentMethod } from '../../../constants/order'
import { onlyNumbers } from '../../../utils/onlyNumbers'

export const resetState = () => ({
  type: signUpTypes.RESET_STATE
})

export const setActualStep = (actualStep) => ({
  type: signUpTypes.SET_ACTUAL_STEP,
  payload: {
    actualStep
  }
})

export const setStep1 = (data) => ({
  type: signUpTypes.SET_STEP1,
  payload: {
    ...data
  }
})

export const setStep2 = (data) => ({
  type: signUpTypes.SET_STEP2,
  payload: {
    ...data
  }
})

export const setStep3 = (data) => ({
  type: signUpTypes.SET_STEP3,
  payload: {
    ...data
  }
})

export const setStep4 = (data) => ({
  type: signUpTypes.SET_STEP4,
  payload: {
    ...data
  }
})

export const setStep5 = (data) => ({
  type: signUpTypes.SET_STEP5,
  payload: {
    ...data
  }
})

export const nextStep = () => (dispatch, getState) => {
  const { signUp } = getState()
  const { actualStep } = signUp

  if (actualStep !== 5) {
    dispatch(setError(''))
    dispatch(setActualStep(actualStep + 1))
  }
}

export const prevStep = () => (dispatch, getState) => {
  const { signUp } = getState()
  const { actualStep } = signUp

  if (actualStep !== 1) {
    dispatch(setError(''))
    dispatch(setActualStep(actualStep - 1))
  }
}

export const setError = (error) => ({
  type: signUpTypes.SET_ERROR,
  payload: {
    error
  }
})

export const fetchProducts = () => async (dispatch, getState) => {
  const { step3 } = getState().signUp

  // Clear error
  dispatch(setError(''))

  try {
    const { data: products } = await ecbrApi.products.getAll()

    await dispatch(
      setStep3({
        ...step3,
        products
      })
    )
  } catch (err) {
    console.log({ err })
    const message = 'Ocorreu um erro desconhecido ao buscar os produtos'
    const errorMessage = err?.response?.data?.message || message
    dispatch(setError(errorMessage))
  }
}

export const pay = (paymentMethod) => async (dispatch, getState) => {
  const signUpState = getState().signUp
  const { step5 } = signUpState
  dispatch(setError(''))

  const signUpPayload = generateSignUpPurchasePayload(
    signUpState,
    paymentMethod
  )

  let purchase = null
  try {
    const { data: _purchase } = await ecbrApi.signUp.create(signUpPayload)
    purchase = _purchase
  } catch (err) {
    const { errors } = err?.response?.data ?? {}
    let errorMessage = 'Ocorreu um erro ao gerar a fatura.\n'

    if (errors?.length) {
      errorMessage = generateErrorMessage(errors, errorMessage, signUpPayload)
    }

    dispatch(setError(errorMessage))
    return false
  }

  if (!purchase?.order.success) {
    return false
  }

  dispatch(setStep5({ ...step5, purchase }))
  return true
}

export const payWithExistingUser =
  (paymentMethod, user) => async (dispatch, getState) => {
    const signUpState = getState().signUp
    const { step5 } = signUpState
    dispatch(setError(''))

    const signUpPayload = generateSignupPurchaseNewBrandPayload(
      signUpState,
      paymentMethod,
      user
    )

    let purchase = null
    try {
      const { data: _purchase } = await ecbrApi.signUp.createBrand(
        signUpPayload
      )
      purchase = _purchase
    } catch (err) {
      const { errors } = err?.response?.data ?? {}
      let errorMessage = 'Ocorreu um erro ao gerar a fatura.\n'

      if (errors?.length) {
        errorMessage = generateErrorMessage(errors, errorMessage, signUpPayload)
      }

      dispatch(setError(errorMessage))
      return false
    }

    if (!purchase?.order.success) {
      return false
    }

    dispatch(setStep5({ ...step5, purchase }))
    return true
  }

const createBrand = (company) => async (dispatch, getState) => {
  const { step1 } = getState().signUp

  const brandPayload = {
    brandName: step1.brandName,
    urlWebsite: step1.url,
    status: 'ACTIVE',
    company: {
      id: company.id
    }
  }

  let brand = null
  try {
    const { data: _brand } = await ecbrApi.brands.create(brandPayload)
    brand = _brand
    /* brand example:
      {
        "brandName": "Teste Nova Loja",
        "urlWebsite": "novaloja.com.br",
        "status": "ACTIVE",
        "company": {
          "id": 9
        },
        "context": {
          "params": {},
          "query": {},
          "user": {
            "id": 11,
            "name": "Teste ",
            "email": "teste10@teste.com",
            "role": "ADMIN_USER",
            "companies": [
              {
                "id": 9,
                "corporateName": "Teste Dez LTDA",
                "cnpj": "17090308000178",
                "urlWebsite": "www.teste10.com",
                "address": "Rua Vergueiro, 10000",
                "zipCode": "01504-000",
                "city": "São Paulo",
                "state": "SP",
                "country": "BR",
                "status": "ACTIVE",
                "createdAt": "2022-04-03T01:50:07.626Z",
                "updatedAt": "2022-04-03T01:50:07.626Z",
                "brands": [
                  {
                    "id": 9,
                    "brandName": "Teste Dez",
                    "urlWebsite": "www.teste10.com",
                    "status": "ACTIVE",
                    "companyId": 9,
                    "createdAt": "2022-04-03T01:50:07.626Z",
                    "updatedAt": "2022-04-03T01:50:07.626Z"
                  }
                ]
              }
            ],
            "isUserCompany": true
          }
        },
        "companyId": 9,
        "id": 10,
        "createdAt": "2022-04-05T19:23:27.818Z",
        "updatedAt": "2022-04-05T19:23:27.818Z"
      }
      */
  } catch (err) {
    const { errors } = err?.response?.data ?? {}
    let errorMessage = 'Ocorreu um erro ao criar a loja.\n'

    if (errors?.length) {
      errorMessage = generateErrorMessage(errors, errorMessage, brandPayload)
    }

    dispatch(setError(errorMessage))
    return null
  }

  if (!brand?.id) {
    return null
  }

  return brand
}

const buyCertificate =
  (company, brand, paymentMethod) => async (dispatch, getState) => {
    const { step3, step4 } = getState().signUp

    const product = step3.productSelected
    const expirationDate = step4.creditCard.cardExpiringDate
    const expMonth = Number(expirationDate.substring(0, 2))
    const expYear = Number(expirationDate.substring(3, 5))

    const certificateOrderPayload = {
      items: [
        {
          quantity: 1,
          code: product.id
        }
      ],
      companyId: company.id,
      brandId: brand.id,
      payments: [
        {
          paymentMethod,
          ...(paymentMethod === PaymentMethod.CREDIT_CARD && {
            creditCard: {
              recurrence: false,
              installments: Number(step4.creditCard.installments),
              statementDescriptor: 'ECBR',
              card: {
                number: onlyNumbers(step4.creditCard.cardNumber),
                holderName: step4.creditCard.cardHolderName,
                expMonth,
                expYear,
                cvv: step4.creditCard.cardCvv
              }
            }
          })
        }
      ],
      ...(step4.coupon && { coupon: step4.coupon })
    }

    let certificateOrder = null
    try {
      const { data: _certificateOrder } = await ecbrApi.orders.purchase(
        certificateOrderPayload
      )
      certificateOrder = _certificateOrder
      /* certificateOrder example:
      {
        "success": true,
        "message": "PAGAMENTO REALIZADO COM SUCESSO.",
        "bankSlip": {
            "url": "https://pagar.me",
            "pdf": "https://pagar.me?format=pdf",
            "line": "1234 5678"
        }
      }
      */
    } catch (err) {
      const { errors } = err?.response?.data ?? {}
      let errorMessage = 'Ocorreu um erro ao criar a fatura.\n'

      if (errors?.length) {
        errorMessage = generateErrorMessage(
          errors,
          errorMessage,
          certificateOrderPayload
        )
      }

      dispatch(setError(errorMessage))
      return false
    }

    if (!certificateOrder?.success) {
      return false
    }

    return certificateOrder
  }

export const createNewBrandAndBuyCertificate =
  (paymentMethod, user) => async (dispatch, getState) => {
    const [company] = user.companies || []
    const { step5 } = getState().signUp

    const _createBrand = createBrand(company)
    const brand = await _createBrand(dispatch, getState)

    if (!brand?.id) {
      return false
    }

    const _buyCertificate = buyCertificate(company, brand, paymentMethod)
    const certificateOrder = await _buyCertificate(dispatch, getState)

    dispatch(setStep5({ ...step5, purchase: { order: certificateOrder } }))
    return true
  }
